import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Pagination,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Menu,
    MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Loader from "../../Components/Loader";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

const FAQs = () => {
    const pageSize = 10;
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [faqData, setFaqData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [useDummyData, setUseDummyData] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingFaq, setEditingFaq] = useState(null);
    const [formData, setFormData] = useState({ question: "", answer: "" });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFaq, setSelectedFaq] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Dummy fallback data
    const dummyData = [
        {
            id: 1,
            question: "How does the dog community feature work?",
            answer: "The dog community allows pet owners to create profiles, share updates, ask questions, join discussions, and connect with other dog lovers in their area.",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 2,
            question: "Can I track my dog's walk or training activity?",
            answer: "Absolutely. The app provides real-time tracking, activity summaries, and detailed reports including walk duration, distance, and training progress.",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 3,
            question: "How do I create a profile for my pet?",
            answer: "You can easily create a pet profile by going to the 'My Pets' section, clicking 'Add Pet', and filling in your pet's information including name, breed, age, and photos.",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 4,
            question: "Is the app free to use?",
            answer: "The basic features of TreatWalk are free to use. Premium features like advanced analytics and unlimited photo storage are available with a subscription.",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 5,
            question: "How do I connect with other dog owners?",
            answer: "You can connect with other dog owners through the community feed, local events, nearby walks feature, and by joining breed-specific groups.",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        }
    ];

    const getFAQList = async () => {
        setLoading(true);
        try {
            const count = (page - 1) * pageSize;
            const params = {
                count,
                limit: pageSize,
            };
            const response = await AxiosInstance.get(ApiList.GET_FAQS, params);
            if (response?.data?.s === 1 && response?.data?.r?.length > 0) {
                setFaqData(response?.data?.r);
                setUseDummyData(false);
                if (params.skip === 0) {
                    const totalCount = response?.data?.total || 0;
                    localStorage.setItem("faqTotalCount", totalCount);
                    setTotalData(totalCount);
                } else {
                    const totalCountFromStorage = localStorage.getItem("faqTotalCount");
                    setTotalData(totalCountFromStorage ? Number(totalCountFromStorage) : 0);
                }
            } else {
                // Use dummy data if no data from API
                setUseDummyData(true);
                const start = (page - 1) * pageSize;
                const end = start + pageSize;
                setFaqData(dummyData.slice(start, end));
                setTotalData(dummyData.length);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching FAQ data:", error);
            // Use dummy data on error
            setUseDummyData(true);
            const start = (page - 1) * pageSize;
            const end = start + pageSize;
            setFaqData(dummyData.slice(start, end));
            setTotalData(dummyData.length);
            setLoading(false);
        }
    };
    const handlePageChange = (event, value) => {
        setPage(value);
    };
    const handleAddFaq = () => {
        setEditingFaq(null);
        setFormData({ question: "", answer: "" });
        setOpenDialog(true);
    };
    const handleEditFaq = (faq) => {
        setEditingFaq(faq);
        setFormData({ question: faq.question, answer: faq.answer });
        setOpenDialog(true);
        setAnchorEl(null);
    };
    const handleDeleteFaq = async (faqId) => {
        if (deleting) return; // Prevent multiple calls
        setDeleting(true);
        try {
            const formData = new FormData();
            formData.append('faq_id', faqId);
            formData.append('status', '0'); // 0 for delete  
            const response = await AxiosInstance.post(ApiList.EDIT_FAQ, formData);
            if (response?.data?.s === 1) {
                toast.success("FAQ deleted successfully");
                getFAQList(); // Refresh the list
            } else {
                toast.error("Failed to delete FAQ");
            }
        } catch (error) {
            console.error("Error deleting FAQ:", error);
            toast.error("Error deleting FAQ");
        } finally {
            setDeleting(false);
        }
        setAnchorEl(null);
    };

    const handleSaveFaq = async () => {
        if (saving) return; // Prevent multiple calls
        
        if (!formData.question.trim() || !formData.answer.trim()) {
            toast.error("Please fill in both question and answer");
            return;
        }

        setSaving(true);
        try {
            const submitData = new FormData();
            if (editingFaq) {
                // Update existing FAQ
                submitData.append('faq_id', editingFaq.id);
                submitData.append('question', formData.question);
                submitData.append('answer', formData.answer);
                submitData.append('status', '1');
                
                const response = await AxiosInstance.post(ApiList.EDIT_FAQ, submitData);
                if (response?.data?.s === 1) {
                    toast.success("FAQ updated successfully");
                    getFAQList();
                    setOpenDialog(false);
                } else {
                    toast.error("Failed to update FAQ");
                }
            } else {
                // Add new FAQ
                submitData.append('question', formData.question);
                submitData.append('answer', formData.answer);
                
                const response = await AxiosInstance.post(ApiList.ADD_FAQ, submitData);
                if (response?.data?.s === 1) {
                    toast.success("FAQ added successfully");
                    getFAQList();
                    setOpenDialog(false);
                } else {
                    toast.error("Failed to add FAQ");
                }
            }
        } catch (error) {
            console.error("Error saving FAQ:", error);
            toast.error("Error saving FAQ");
        } finally {
            setSaving(false);
        }
    };

    const handleMenuClick = (event, faq) => {
        setAnchorEl(event.currentTarget);
        setSelectedFaq(faq);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedFaq(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        getFAQList();
    }, [page]);

    return (
        <Box
            sx={{
                width: "100%",
                height: "100vh",
                background: "#B9CC0310",
                overflow: "hidden",
            }}
        >
            <Box sx={{ width: "100%", p: 2, background: "#B9CC0310", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    sx={{
                        fontSize: { xs: "18px", md: "28px" },
                        fontWeight: "300",
                        color: "#000",
                    }}
                >
                    FAQs
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddFaq}
                    sx={{
                        background: "#c6ff00",
                        color: "black",
                        fontWeight: "600",
                        "&:hover": {
                            background: "#b5ee00",
                        },
                    }}
                >
                    Add FAQ
                </Button>
            </Box>
            <Box sx={{ width: "100%", height: "calc(100vh - 74px)" }}>
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        p: 2,
                        overflow: "auto",
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: "400px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Loader />
                        </Box>
                    ) : faqData.length === 0 ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: "400px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Typography sx={{ color: "#FFFFFF60", fontSize: "18px" }}>
                                No FAQ data available
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}>
                            {faqData.map((faq, index) => (
                                <Accordion
                                    key={faq.id || index}
                                    sx={{
                                        background: "#fff",
                                        borderRadius: "10px !important",
                                        border: "1px solid #FFFFFF10",
                                        "&:before": {
                                            display: "none",
                                        },
                                        "&.Mui-expanded": {
                                            margin: "0 !important",
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: "#c6ff00" }} />}
                                        sx={{
                                            minHeight: "70px !important",
                                            "&.Mui-expanded": {
                                                minHeight: "70px !important",
                                            },
                                            "& .MuiAccordionSummary-content": {
                                                margin: "12px 0 !important",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                pr: 2,
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        color: "#000",
                                                        fontSize: "16px",
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    {faq.question}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: "right", display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            color: "#000",
                                                            fontSize: "12px",
                                                            fontWeight: "500",
                                                        }}
                                                    >
                                                        Created
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: "#000",
                                                            fontSize: "11px",
                                                        }}
                                                    >
                                                        {formatDate(faq.created_at)}
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMenuClick(e, faq);
                                                    }}
                                                    sx={{ color: "#000" }}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                        <Box
                                            sx={{
                                                background: "#b9cc0360",
                                                borderRadius: "8px",
                                                p: 2,
                                                // borderLeft: "3px solid #c6ff00",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#000",
                                                    fontSize: "14px",
                                                    fontWeight: "600",
                                                    mb: 1,
                                                }}
                                            >
                                                Answer : 
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "#000",
                                                    fontSize: "14px",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {faq.answer}
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}

                            {/* Pagination */}
                            {totalData > pageSize && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mt: 3,
                                        mb: 2,
                                    }}
                                >
                                    <Pagination
                                        count={Math.ceil(totalData / pageSize)}
                                        page={page}
                                        onChange={handlePageChange}
                                        sx={{
                                            "& .MuiPaginationItem-root": {
                                                color: "white",
                                                borderColor: "#FFFFFF30",
                                            },
                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                background: "#c6ff00",
                                                color: "black",
                                                fontWeight: "600",
                                            },
                                            "& .MuiPaginationItem-root:hover": {
                                                backgroundColor: "rgba(59, 198, 241, 0.1)",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={saving ? undefined : () => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: "#fff",
                        color: "white",
                    }
                }}
            >
                <DialogTitle sx={{ color: "#000", borderBottom: "1px solid #00000020" }}>
                    {editingFaq ? "Edit FAQ" : "Add New FAQ"}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Question"
                        value={formData.question}
                        onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                        margin="normal"
                        multiline
                        rows={2}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                color: "#000",
                                "& fieldset": { borderColor: "#000" },
                                "&:hover fieldset": { borderColor: "#000" },
                                "&.Mui-focused fieldset": { borderColor: "#000" },
                            },
                            "& .MuiInputLabel-root": { color: "#000" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Answer"
                        value={formData.answer}
                        onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                        margin="normal"
                        multiline
                        rows={4}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                color: "#000",
                                "& fieldset": { borderColor: "#000" },
                                "&:hover fieldset": { borderColor: "#000" },
                                "&.Mui-focused fieldset": { borderColor: "#000" },
                            },
                            "& .MuiInputLabel-root": { color: "#000" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: "1px solid #00000020" }}>
                    <Button 
                        onClick={() => setOpenDialog(false)}
                        disabled={saving}
                        sx={{ color: saving ? "#999999" : "#000" }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveFaq}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: saving ? "#999999" : "#c6ff00",
                            color: "black",
                            fontWeight: "600",
                            "&:hover": {
                                background: saving ? "#999999" : "#b5ee00",
                            },
                        }}
                    >
                        {saving ? "Saving..." : (editingFaq ? "Update" : "Add")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: "#fff",
                        color: "white",
                        border: "1px solid #FFFFFF20",
                    }
                }}
            >
                <MenuItem 
                    onClick={() => handleEditFaq(selectedFaq)}
                    sx={{ color: "#000", "&:hover": { background: "#FFFFFF10" } }}
                >
                    <EditIcon sx={{ mr: 1, fontSize: "18px" }} />
                    Edit
                </MenuItem>
                <MenuItem 
                    onClick={() => handleDeleteFaq(selectedFaq?.id)}
                    disabled={deleting}
                    sx={{ 
                        color: deleting ? "#999999" : "#ff4444", 
                        "&:hover": { background: "#FFFFFF10" } 
                    }}
                >
                    <DeleteIcon sx={{ mr: 1, fontSize: "18px" }} />
                    {deleting ? "Deleting..." : "Delete"}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default FAQs;